import React, { useState, useEffect } from 'react';
import { appRepository, GardenState } from '../../core/storage/repository';
import { speechProvider } from '../../core/speech/speechProvider';
import { RaraMascot } from '../../components/mascot/RaraMascot';
import { PixiGameIsland } from '../../core/games/PixiGameIsland';
import confetti from 'canvas-confetti';

interface GardenViewProps {
  onBack: () => void;
}

const STAGES = [
  { name: 'Benih Baru', emoji: '🌰', desc: 'Benih ajaib yang baru ditanam di tanah subur.' },
  { name: 'Kecambah Kecil', emoji: '🌱', desc: 'Tunas hijau kecil mulai menyapa matahari!' },
  { name: 'Tanaman Muda', emoji: '🪴', desc: 'Daun-daun segarmu tumbuh semakin lebat.' },
  { name: 'Bunga Mekar', emoji: '🌸', desc: 'Bunga indah bermekaran karena kamu rajin membaca!' },
  { name: 'Pohon Rindang', emoji: '🌳', desc: 'Pohon belajar tumbuh kokoh dan memberi keteduhan.' },
  { name: 'Pohon Berbuah Manis', emoji: '🍎', desc: 'Pohonmu menghasilkan buah membaca yang melimpah!' },
];

export const GardenView: React.FC<GardenViewProps> = ({ onBack }) => {
  const [garden, setGarden] = useState<GardenState>(appRepository.getState().garden);
  const [isWatering, setIsWatering] = useState(false);
  const [mascotMessage, setMascotMessage] = useState('Lihat kebun belajarmu tumbuh setiap kali kamu belajar!');

  useEffect(() => {
    const unsub = appRepository.subscribe(state => setGarden(state.garden));
    return unsub;
  }, []);

  const currentStage = STAGES[Math.min(garden.stage, STAGES.length - 1)];

  const handleWaterPlant = () => {
    setIsWatering(true);
    speechProvider.playChime('water');
    setMascotMessage('Segar sekali! Tanamanmu bertambah subur!');
    speechProvider.speak('Segar sekali! Tanamanmu bertambah subur!', { rate: 0.9 });
    confetti({ particleCount: 30, spread: 60, origin: { y: 0.7 } });

    setTimeout(() => {
      setIsWatering(false);
    }, 1200);
  };

  return (
    <div className="flex flex-col min-h-screen bg-[#F0FDF4] pb-12">
      {/* Header */}
      <div className="sticky top-0 z-30 bg-white/90 backdrop-blur-md border-b-2 border-emerald-100 px-4 py-3 flex items-center justify-between">
        <button
          onClick={() => {
            speechProvider.playChime('tap');
            onBack();
          }}
          className="flex items-center gap-2 bg-emerald-100 hover:bg-emerald-200 active:scale-95 text-emerald-900 font-bold px-4 py-2.5 rounded-2xl transition"
        >
          <span className="text-xl">⬅️</span>
          <span className="text-sm md:text-base">Kembali</span>
        </button>

        <h1 className="text-xl md:text-2xl font-display font-bold text-emerald-600 flex items-center gap-2">
          <span>🌱</span> Kebun Belajarku
        </h1>

        <div className="flex items-center gap-1.5 bg-emerald-100 text-emerald-900 px-3 py-1.5 rounded-2xl font-bold text-sm">
          <span>💧 {garden.waterCount} Air</span>
        </div>
      </div>

      <div className="max-w-3xl mx-auto w-full px-4 pt-4 flex-1 flex flex-col gap-6">
        <RaraMascot
          state="happy"
          message={mascotMessage}
          size="sm"
          onBubbleClick={() => speechProvider.speak(mascotMessage, { rate: 0.88 })}
        />

        {/* Garden Centerpiece Card with PixiJS Engine */}
        <div className="bg-white rounded-3xl p-6 md:p-8 shadow-xl border-4 border-emerald-300 flex flex-col items-center text-center relative overflow-hidden">
          <div className="text-xs font-bold text-emerald-700 bg-emerald-100 px-3 py-1 rounded-full uppercase tracking-wider mb-2">
            Tahap {garden.stage + 1} dari 6: {currentStage.name}
          </div>

          <h2 className="text-2xl md:text-3xl font-display font-black text-[#243047] mb-1">
            {currentStage.name}
          </h2>
          <p className="text-xs md:text-sm text-gray-600 max-w-md mb-4">
            {currentStage.desc}
          </p>

          {/* Interactive PixiJS Garden Stage */}
          <PixiGameIsland
            key={`garden-${garden.stage}`}
            gameMode="garden"
            targetValue=""
            className="my-1"
          />

          {/* Water Button */}
          <button
            onClick={handleWaterPlant}
            disabled={isWatering}
            className="mt-6 px-8 py-4 bg-emerald-500 hover:bg-emerald-600 active:scale-95 text-white font-display font-extrabold text-lg md:text-xl rounded-full shadow-lg flex items-center gap-3 transition"
          >
            <span className="text-2xl">🚿</span>
            <span>Siram Tanaman</span>
          </button>

          {/* Growth Bar */}
          <div className="w-full max-w-sm mt-6">
            <div className="flex justify-between text-xs font-bold text-gray-500 mb-1.5">
              <span>Pertumbuhan Tanaman</span>
              <span>{Math.min(100, Math.round(((garden.stage + 1) / 6) * 100))}%</span>
            </div>
            <div className="w-full h-3.5 bg-emerald-100 rounded-full overflow-hidden border border-emerald-300">
              <div
                className="h-full bg-emerald-500 rounded-full transition-all duration-500"
                style={{ width: `${Math.min(100, ((garden.stage + 1) / 6) * 100)}%` }}
              />
            </div>
          </div>
        </div>

        {/* Badges Collection */}
        <div className="bg-white rounded-3xl p-6 shadow-md border-2 border-emerald-200">
          <h3 className="text-base font-bold text-[#243047] mb-3 flex items-center gap-2">
            <span>🏅 Lencana Prestasi Belajarmu:</span>
          </h3>
          <div className="flex flex-wrap gap-2.5">
            {garden.unlockedBadges.map((badge, idx) => (
              <span
                key={idx}
                className="px-3.5 py-1.5 rounded-full bg-amber-50 text-amber-900 border-2 border-amber-200 font-bold text-xs flex items-center gap-1.5 shadow-2xs"
              >
                <span>⭐</span>
                <span>{badge}</span>
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
